import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/http/api.service';
import { ImageService } from '@app/core/http/image.service';
import { IOcbCUEnvironmentAdministrator, IOcbCUGlobalAdministrator, IOcbEnvironmentAdministrator, IOcbGlobalAdministrator } from '@app/core/models';
import { environment } from '@env/environment';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

@Injectable()
export class AdministratorsService extends ApiService {

    private globalAdministratorsUrl: string;
    private environmentsUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient, private imageService: ImageService) {
        super(httpClient);
        this.globalAdministratorsUrl = `${environment.api.endpoint}/GlobalAdministrators`;
        this.environmentsUrl = `${environment.api.endpoint}/Environments`;
    }

    /**
     * Get an administrator of an environment.
     * @param environmentId Internal identifier of the environment.
     * @param employeeId EDA Internal identifier of the employee.
     * @returns Observable of IOcbEnvironmentAdministrator.
     */
    public getEnvironmentAdministrator(environmentId: string, employeeId: string): Observable<IOcbEnvironmentAdministrator> {
        const url = `${this.environmentsUrl}/${environmentId}/Administrators/${employeeId}`;
        return this.httpClient.get<IOcbEnvironmentAdministrator>(url, this.options);
    }

    /**
     * Get the list of administrators of an environment.
     * @param environmentId Internal identifier of the environment.
     * @returns Observable of IOcbEnvironmentAdministrator[].
     */
    public getEnvironmentAdministrators(environmentId: string): Observable<IOcbEnvironmentAdministrator[]> {
        const url = `${this.environmentsUrl}/${environmentId}/Administrators`;
        return this.httpClient.get(url, this.options)
            .pipe(mergeMap((admins: IOcbEnvironmentAdministrator[]) => {
                if (admins.length > 0) {
                    return forkJoin(
                        admins.map((admin: IOcbEnvironmentAdministrator) => {
                            return forkJoin([
                                this.httpClient.get(`https://graph.microsoft.com/beta/users/${admin.user.id}`).pipe(catchError(_ => of(null))),
                                this.imageService.get(admin.user.id).pipe(catchError(_ => of('../../assets/images/no-user-photo.jpg'))),
                            ])
                                .pipe(
                                    map((data: any[]) => {
                                        if (data.length > 0) admin.user.properties = data[0];
                                        if (data.length > 1) admin.user.photo = data[1];
                                        return admin;
                                    }),
                                    catchError(_ => of(admin)))
                        }));
                }
                else return of([] as IOcbEnvironmentAdministrator[]);
            }));
    }

    /**
     * Add an administrator to an environment.
     * @param environmentId Internal identifier of the environment.
     * @param employeeId EDA Internal identifier of the employee.
     * @param permissions Permissions of the administrator to create
     * @returns Observable of IOcbEnvironmentAdministrator. Contains the new administrator
     */
    public addEnvironmentAdministrator(environmentId: string, employeeId: string, permissions: IOcbCUEnvironmentAdministrator): Observable<IOcbEnvironmentAdministrator> {
        const url = `${this.environmentsUrl}/${environmentId}/Administrators/${employeeId}`;
        return this.httpClient.post<IOcbEnvironmentAdministrator>(url, permissions, this.options);
    }

    /**
     * Update an environment administrator permissions.
     * @param environmentId Internal identifier of the environment.
     * @param employeeId EDA Internal identifier of the employee.
     * @param permissions New permissions of the administrator
     * @returns Observable of IOcbEnvironmentAdministrator. Contains the updated administrator
     */
    public updateEnvironmentAdministrator(environmentId: string, employeeId: string, permissions: IOcbCUEnvironmentAdministrator): Observable<IOcbEnvironmentAdministrator> {
        const url = `${this.environmentsUrl}/${environmentId}/Administrators/${employeeId}`;
        return this.httpClient.put<IOcbEnvironmentAdministrator>(url, permissions, this.options);
    }

    /**
     * Delete an environment administrator.
     * @param environmentId Internal identifier of the environment.
     * @param employeeId EDA Internal identifier of the employee.
     * @returns Empty observable.
     */
    public deleteEnvironmentAdministrator(environmentId: string, employeeId: string): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/Administrators/${employeeId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Get a global administrator.
     * @param employeeId EDA Internal identifier of the employee.
     * @returns Observable of IOcbGlobalAdministrator.
     */
    public getGlobalAdministrator(employeeId: string): Observable<IOcbGlobalAdministrator> {
        const url = `${this.globalAdministratorsUrl}/${employeeId}`;
        return this.httpClient.get<IOcbGlobalAdministrator>(url, this.options);
    }

    /**
     * Get the list of global administrators.
     * @returns Observable of IOcbGlobalAdministrator[].
     */
    public getGlobalAdministrators(): Observable<IOcbGlobalAdministrator[]> {
        return this.httpClient.get<IOcbGlobalAdministrator[]>(this.globalAdministratorsUrl, this.options)
            .pipe(mergeMap((admins: IOcbGlobalAdministrator[]) => {
                if (admins.length > 0) {
                    return forkJoin(
                        admins.map((admin: IOcbGlobalAdministrator) => {
                            return forkJoin([
                                this.httpClient.get(`https://graph.microsoft.com/beta/users/${admin.user.id}`).pipe(catchError(_ => of(null))),
                                this.imageService.get(admin.user.id).pipe(catchError(_ => of('../../assets/images/no-user-photo.jpg'))),
                            ])
                                .pipe(
                                    map((data: any[]) => {
                                        if (data.length > 0) admin.user.properties = data[0];
                                        if (data.length > 1) admin.user.photo = data[1];
                                        return admin;
                                    }),
                                    catchError(_ => of(admin)))
                        }));
                }
                else return of([] as IOcbGlobalAdministrator[]);
            }));
    }

    /**
     * Add a global administrator.
     * @param employeeId EDA Internal identifier of the employee.
     * @param permissions Permissions of the administrator to create
     * @returns Observable of IOcbGlobalAdministrator.
     */
    public addGlobalAdministrator(employeeId: string, permissions: IOcbCUGlobalAdministrator): Observable<IOcbGlobalAdministrator> {
        const url = `${this.globalAdministratorsUrl}/${employeeId}`;
        return this.httpClient.post<IOcbGlobalAdministrator>(url, permissions, this.options);
    }

    /**
     * Update the permissions of a global administrator.
     * @param employeeId EDA Internal identifier of the employee.
     * @param permissions New rights of the administrator.
     * @returns Observable of IOcbGlobalAdministrator.
     */
    public updateGlobalAdministrator(employeeId: string, permissions: IOcbCUGlobalAdministrator): Observable<IOcbGlobalAdministrator> {
        const url = `${this.globalAdministratorsUrl}/${employeeId}`;
        return this.httpClient.put<IOcbGlobalAdministrator>(url, permissions, this.options);
    }

    /**
     * Delete a global administrator.
     * @returns Empty Observable.
     */
    public deleteGlobalAdministrator(employeeId: string): Observable<void> {
        const url = `${this.globalAdministratorsUrl}/${employeeId}`;
        return this.httpClient.delete<void>(url, this.options);
    }
}
